import { getPollStatus } from "@/shared/utils/polls";
import {
  Poll,
  PollResponse,
  PollResponsesResponse,
  PollResults,
  PollResultsSummary,
  PollStatus,
} from "@/shared/utils/interfaces";
import dayjs from "dayjs";
import { getMockPoll } from "./pollsMock";

/**
 * Temporary backend for voting on polls and their results, see `pollsMock.ts`.
 *
 * Every poll gets generated responses from fake customers, the same ones on
 * every call, so the results look filled. Real votes cast in this browser are
 * kept in localStorage per user and are added on top, so voting in a system
 * message shows up in the admin results too. The real endpoints will know the
 * user from the session.
 */

const VOTES_KEY_PREFIX = "mock_poll_votes_";
const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";

interface StoredVote {
  option_ids: number[];
  voted_at: string;
}

type Votes = Record<number, StoredVote>;

const delay = <T>(value: T, ms = 300): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

const reject = (status: number, errorId: string) =>
  Promise.reject({ response: { status, data: { error_id: errorId } } });

// ---------------------------------------------------------------------------
// Votes of users in this browser
// ---------------------------------------------------------------------------

const readVotes = (key: string): Votes => {
  try {
    const votes = JSON.parse(window.localStorage.getItem(key) ?? "{}");
    return votes && typeof votes === "object" ? votes : {};
  } catch {
    return {};
  }
};

const readUserVotes = (userId: number) =>
  readVotes(`${VOTES_KEY_PREFIX}${userId}`);

const writeUserVotes = (userId: number, votes: Votes) => {
  try {
    window.localStorage.setItem(
      `${VOTES_KEY_PREFIX}${userId}`,
      JSON.stringify(votes)
    );
  } catch {
    // Storage full or blocked
  }
};

// Every user that voted in this browser, as responses
const getLocalResponses = (poll: Poll): PollResponse[] => {
  const responses: PollResponse[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(VOTES_KEY_PREFIX)) continue;
      const userId = Number(key.slice(VOTES_KEY_PREFIX.length));
      const vote = readVotes(key)[poll.item_id];
      if (!vote?.option_ids?.length) continue;
      responses.push({
        item_id: 1_000_000 + userId,
        poll_id: poll.item_id,
        branch_id: 0,
        branch_name: "-",
        user_name: `#${userId}`,
        option_ids: vote.option_ids,
        created_at: vote.voted_at,
      });
    }
  } catch {
    // Storage unavailable, only generated responses
  }
  return responses;
};

// ---------------------------------------------------------------------------
// Generated responses
// ---------------------------------------------------------------------------

const BRANCHES = [
  "Pizzeria Napoli Utrecht",
  "Sushi Garden Amsterdam",
  "Burger Bar Rotterdam",
  "Döner Palace Den Haag",
  "Wok To Go Eindhoven",
  "Grillroom Istanbul Tilburg",
  "Pasta e Basta Groningen",
  "Snackbar De Hoek Breda",
  "Thai Orchid Nijmegen",
  "Shoarma Express Arnhem",
  "Poké Bowl Haarlem",
  "Lunchroom Zoet Leiden",
  "Pizza Roma Almere",
  "Kebab House Zwolle",
  "Indian Spice Delft",
];

const FIRST_NAMES = ["Sanne", "Mehmet", "Lisa", "Daan", "Fatima", "Bram", "Emma", "Yusuf", "Noah", "Julia", "Ahmed", "Sophie"];
const LAST_NAMES = ["de Vries", "Yilmaz", "Jansen", "Bakker", "El Amrani", "Visser", "Smit", "Demir", "Mulder", "de Boer"];

// Small seeded random so a poll always gets the same responses
const seededRandom = (seed: number) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const pick = <T>(random: () => number, items: T[]) =>
  items[Math.floor(random() * items.length)];

// Picks an index with a chance in proportion to its weight
const pickWeighted = (random: () => number, weights: number[]) => {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let roll = random() * total;
  for (let i = 0; i < weights.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return i;
  }
  return weights.length - 1;
};

const generateResponses = (poll: Poll): PollResponse[] => {
  const start = dayjs(poll.start_time);
  // Responses can only exist between the start and now or the end
  const until = dayjs().isBefore(dayjs(poll.end_time)) ? dayjs() : dayjs(poll.end_time);
  const span = until.diff(start, "minute");
  if (span <= 0 || !poll.options.length) return [];

  const random = seededRandom(poll.item_id * 7919 + poll.options.length);
  // About 4 a day, at most 80, so short polls stay believable
  const count = Math.min(80, Math.max(3, Math.round((span / 1440) * (2 + random() * 4))));
  // Uneven weights so there is a clear favourite
  const weights = poll.options.map(() => 0.3 + random() ** 2 * 3);

  return Array.from({ length: count }, (_, index) => {
    const chosen = new Set([pickWeighted(random, weights)]);
    if (poll.can_choose_multiple_options) {
      while (random() < 0.4 && chosen.size < poll.options.length) {
        chosen.add(pickWeighted(random, weights));
      }
    }
    // Most answers arrive in the first days after the start
    const offset = Math.floor(random() ** 1.8 * span);
    const branchIndex = Math.floor(random() * BRANCHES.length);

    return {
      item_id: poll.item_id * 1000 + index + 1,
      poll_id: poll.item_id,
      branch_id: branchIndex + 1,
      branch_name: BRANCHES[branchIndex],
      user_name: `${pick(random, FIRST_NAMES)} ${pick(random, LAST_NAMES)}`,
      option_ids: Array.from(chosen)
        .sort((a, b) => a - b)
        .map((i) => poll.options[i].item_id),
      created_at: start.add(offset, "minute").format(DATE_TIME_FORMAT),
    };
  });
};

const getAllResponses = (poll: Poll) =>
  [...generateResponses(poll), ...getLocalResponses(poll)]
    // Newest first
    .sort((a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf());

const countVotes = (poll: Poll, responses: PollResponse[]) =>
  poll.options.map((option) => ({
    option_id: option.item_id,
    votes: responses.filter((r) => r.option_ids.includes(option.item_id)).length,
  }));

// ---------------------------------------------------------------------------
// Customer
// ---------------------------------------------------------------------------

export const getMockPollResults = async (
  pollId: number,
  userId: number
): Promise<PollResults> => {
  const poll = await getMockPoll(pollId);
  const responses = getAllResponses(poll);

  return {
    poll,
    results: countVotes(poll, responses),
    total_responses: responses.length,
    my_option_ids: readUserVotes(userId)[pollId]?.option_ids ?? [],
  };
};

export const voteMockPoll = async (
  pollId: number,
  optionIds: number[],
  userId: number
): Promise<void> => {
  const poll = await getMockPoll(pollId);
  const votes = readUserVotes(userId);
  const validIds = new Set(poll.options.map((option) => option.item_id));

  if (getPollStatus(poll) !== PollStatus.ACTIVE) return reject(422, "pollNotActive");
  if (votes[pollId]?.option_ids?.length) return reject(422, "pollAlreadyVoted");
  if (!optionIds.length || optionIds.some((id) => !validIds.has(id)))
    return reject(422, "pollInvalidOptions");
  if (!poll.can_choose_multiple_options && optionIds.length > 1)
    return reject(422, "pollInvalidOptions");

  writeUserVotes(userId, {
    ...votes,
    [pollId]: { option_ids: optionIds, voted_at: dayjs().format(DATE_TIME_FORMAT) },
  });
  return delay(undefined);
};

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export const getMockPollResultsSummary = async (
  pollId: number
): Promise<PollResultsSummary> => {
  const poll = await getMockPoll(pollId);
  const responses = getAllResponses(poll);
  const votes = countVotes(poll, responses);

  const daily: PollResultsSummary["daily"] = [];
  const lastDay = dayjs().isBefore(dayjs(poll.end_time)) ? dayjs() : dayjs(poll.end_time);
  for (
    let day = dayjs(poll.start_time).startOf("day");
    !day.isAfter(lastDay, "day");
    day = day.add(1, "day")
  ) {
    daily.push({
      date: day.format("YYYY-MM-DD"),
      responses: responses.filter((r) => dayjs(r.created_at).isSame(day, "day")).length,
    });
  }

  return delay({
    poll,
    total_responses: responses.length,
    options: poll.options.map((option) => ({
      option_id: option.item_id,
      text: option.text,
      votes: votes.find((v) => v.option_id === option.item_id)?.votes ?? 0,
    })),
    daily,
  });
};

interface GetPollResponsesParams {
  pollId: number;
  search?: string;
  optionId?: number;
  limit: number;
  offset: number;
}

export const getMockPollResponses = async ({
  pollId,
  search,
  optionId,
  limit,
  offset,
}: GetPollResponsesParams): Promise<PollResponsesResponse> => {
  const poll = await getMockPoll(pollId);
  const term = search?.trim().toLowerCase();
  const filtered = getAllResponses(poll)
    .filter((r) => !optionId || r.option_ids.includes(optionId))
    .filter(
      (r) =>
        !term ||
        r.branch_name.toLowerCase().includes(term) ||
        r.user_name.toLowerCase().includes(term)
    );

  return delay({
    total: filtered.length,
    limit,
    offset,
    items: filtered.slice(offset, offset + limit),
  });
};
